import React, { forwardRef, useRef, useState } from 'react';
import { DocumentUploadCategory, SUBCATEGORY_LABELS } from 'types/Documents';
import { FileType } from 'types/Files';
import UploadIcon from 'assets/icons/upload.svg?react';
import TrashIcon from 'assets/icons/trash.svg?react';
import { formatFileSize, SUPPORTED_FILE_TYPES } from 'utils/documents';
import Modal, { ModalRef } from 'components/Modal/Modal';
import Button from 'components/Button/Button';
import FileTypeIcon from 'components/Icons/FileTypeIcon';
import styles from './UploadDocumentModal.module.css';

type Props = {
    uploadCategories: DocumentUploadCategory[];
    onUpload: (files: File[], category: DocumentUploadCategory) => Promise<void>;
};

const UploadDocumentModal = forwardRef<ModalRef, Props>(({ onUpload, uploadCategories }, ref) => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<DocumentUploadCategory | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const addFiles = (files: FileList | null) => {
        if (!files) return;
        setSelectedFiles((prev) => [...prev, ...Array.from(files)]);
    };

    const removeFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        addFiles(e.target.files);
        e.target.value = '';
    };

    const handleSave = async () => {
        if (!selectedCategory || selectedFiles.length === 0) return;
        setIsSaving(true);
        try {
            await onUpload(selectedFiles, selectedCategory);
            setSelectedFiles([]);
            setSelectedCategory(null);
            (ref as React.RefObject<ModalRef>).current?.closeModal();
        } finally {
            setIsSaving(false);
        }
    };

    const onClose = () => {
        setSelectedFiles([]);
        setSelectedCategory(null);
    };

    const canSave = selectedFiles.length > 0 && selectedCategory !== null && !isSaving;

    return (
        <Modal ref={ref} title="Upload Document" onClose={onClose}>
            <div className={styles.section}>
                <span className={styles.sectionLabel}>Select the File</span>
                {selectedFiles.length > 0 && (
                    <ul className={styles.fileList}>
                        {selectedFiles.map((file, i) => (
                            <li key={i} className={styles.fileRow}>
                                <FileTypeIcon
                                    fileType={(file.name.split('.').pop()?.toLowerCase() ?? '') as FileType}
                                    size={18}
                                />
                                <span className={styles.fileName}>{file.name}</span>
                                <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                                <button
                                    className={styles.removeButton}
                                    onClick={() => removeFile(i)}
                                    aria-label={`Remove ${file.name}`}
                                >
                                    <TrashIcon />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                <div
                    className={`${styles.dropZone} ${isDragging ? styles.dragging : ''}`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    <div className={styles.dropZoneIcon}>
                        <UploadIcon />
                    </div>
                    <p className={styles.dropText}>
                        Drag & Drop or <span className={styles.clickText}>Click to Upload</span>
                    </p>
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={SUPPORTED_FILE_TYPES}
                    className={styles.hiddenInput}
                    onChange={handleInputChange}
                />
            </div>

            <div className={styles.section}>
                <span className={styles.sectionLabel}>Select the Category</span>
                <div className={styles.categoryGrid}>
                    {uploadCategories.map((cat) => (
                        <label key={cat.id} className={styles.radioLabel}>
                            <input
                                type="radio"
                                name="category"
                                value={cat.id}
                                checked={selectedCategory?.id === cat.id}
                                onChange={() => setSelectedCategory(cat)}
                                className={styles.radioInput}
                            />
                            {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                            {SUBCATEGORY_LABELS[cat.sub_category!]}
                        </label>
                    ))}
                </div>
            </div>

            <Button color="primary" onClick={handleSave} disabled={!canSave} loading={isSaving}>
                Save
            </Button>
        </Modal>
    );
});

export default UploadDocumentModal;
